import { Check } from 'lucide-react'
import { MESSAGE_STATUS } from '../../contexts/SSEEvents'
import type { MessageStatus } from '../api'
import { ClarifyIntro } from './ClarifyOverlay'

interface QuestionFormToolData {
  toolName: string
  toolInput: {
    context?: string
    questions?: Array<{
      question: string
      type: string
      required?: boolean
      options?: string[]
    }>
  }
  startedAt: string
  completedAt?: string
  status: 'running' | 'completed'
  componentType?: string
  componentData?: {
    title?: string
    description?: string
    questions?: Array<{
      id: string
      text: string
      type: string
      required?: boolean
      options?: unknown
    }>
  }
  responses?: Record<string, string | string[] | { question: string; answer: string }>
  dismissed?: boolean
}

interface BotMessageToolFormProps {
  toolData: QuestionFormToolData
  status?: MessageStatus
}

/**
 * Read-only feed card for the ask_clarifying_questions tool. While the form
 * is pending, the interactive stepper lives in the composer area
 * (ClarifyOverlay) — this card only previews the questions.
 */
export default function BotMessageToolForm({ toolData, status }: BotMessageToolFormProps) {
  const questions = toolData.componentData?.questions || []
  const intro = toolData.componentData?.description || toolData.componentData?.title

  if (status === MESSAGE_STATUS.FAILED) {
    return (
      <div className="pl-3 py-0.5 border-l-2 border-destructive/40">
        <div className="text-body text-foreground leading-relaxed">
          Failed to load questions. Please try again.
        </div>
      </div>
    )
  }

  if (status === MESSAGE_STATUS.IN_PROGRESS || toolData.status === 'running') {
    if (questions.length === 0) return null

    return (
      <div className="text-body text-foreground">
        {intro && <ClarifyIntro text={intro} className="mb-2" />}
        <div className="flex flex-col gap-1.5 pl-3 border-l-2 border-primary/30">
          {questions.map((q) => (
            <div key={q.id} className="text-caption text-muted-foreground">
              {q.text}
            </div>
          ))}
        </div>
        <span className="text-caption text-muted-foreground mt-1 block">
          Respond below to continue
        </span>
      </div>
    )
  }

  if (status === MESSAGE_STATUS.PROCESSED || toolData.status === 'completed') {
    // Dismissed forms have empty responses — check before the responses guard.
    if (toolData.dismissed) {
      return (
        <div className="text-body text-foreground">
          {intro && <ClarifyIntro text={intro} className="mb-2" />}
          <div className="flex flex-col gap-1.5 pl-3 border-l-2 border-border">
            {questions.map((q) => (
              <div key={q.id} className="text-caption text-muted-foreground">
                {q.text}
              </div>
            ))}
          </div>
          <span className="text-caption text-muted-foreground mt-1 block">Dismissed</span>
        </div>
      )
    }

    const responses = toolData.responses
    if (!responses || Object.keys(responses).length === 0) return null

    return (
      <div className="rounded-lg border border-border bg-card p-3 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Check size={14} strokeWidth={2} className="text-success" />
          <span className="text-label text-foreground">Questions Answered</span>
        </div>

        <div className="flex flex-col gap-3">
          {Object.entries(responses).map(([questionId, response]) => {
            if (!response) return null

            if (
              typeof response === 'object' &&
              !Array.isArray(response) &&
              'question' in response &&
              'answer' in response
            ) {
              return (
                <div key={questionId} className="flex flex-col gap-1">
                  <div className="text-caption text-muted-foreground">{response.question}</div>
                  <div className="text-body text-foreground pl-3 border-l-2 border-primary">
                    {response.answer}
                  </div>
                </div>
              )
            }

            const question = questions.find((q) => q.id === questionId)
            if (!question) return null

            const answerText = Array.isArray(response) ? response.join(', ') : String(response)

            return (
              <div key={questionId} className="flex flex-col gap-1">
                <div className="text-caption text-muted-foreground">{question.text}</div>
                <div className="text-body text-foreground pl-3 border-l-2 border-primary">
                  {answerText}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return null
}
